import {
  Button,
  Col,
  Dropdown,
  Pagination,
  Row,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import "./Data.scss";
import Loader from "./Loader";
import { useEffect, useRef, useState } from "react";
import {
  DataAction,
  DataColumn,
  DataPaging,
  DataRequest,
  DataResult,
  DataSort,
} from "./Data.default";
import { Subscription, subscription } from "../utils/http";
import Icon from "@ant-design/icons";
import { Icons } from "../assets/icons";
import dayjs from "dayjs";
import Editor from "./Editor";

export interface DataProps {
  readonly context: any;
  readonly columns: DataColumn[];
  readonly pageSize: number;
  readonly multipleSort?: boolean;
  readonly hideHeader?: boolean;
  readonly actions?: ((record: any) => DataAction[]) | DataAction[];
  readonly request: (params: DataRequest) => Promise<DataResult>;
  readonly rowKey: (item: any) => any;
  readonly onLoading?: (loading: boolean) => void;
  readonly onDbSelect?: (key: string, record: any, index: number) => void;
}

function Data(props: DataProps) {
  const { token } = theme.useToken();
  const tableRef = useRef<HTMLDivElement>(null!);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [pagination, setPagination] = useState<DataPaging>({
    pageNumber: 0,
    pageSize: 100,
    total: 0,
  });
  const [sort, setSort] = useState<DataSort[]>([]);
  const [subscribe, setSubscribe] = useState<Subscription | null>(null);
  const [height, setHeight] = useState(0);
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);

  useEffect(() => {
    setSelectedKey(null);
    loadData([]);
  }, [props.context]);

  useEffect(() => {
    props.onLoading?.(loading);
  }, [loading]);

  useEffect(() => {
    if (tableRef.current) {
      setHeight(tableRef.current.clientHeight - getHeaderHeight());
    }
  }, [tableRef]);

  useEffect(() => {
    const resizeEvent = () => {
      if (tableRef.current) {
        setHeight(tableRef.current.clientHeight - getHeaderHeight());
      }
    };

    window.addEventListener("resize", resizeEvent);

    return () => {
      window.removeEventListener("resize", resizeEvent);
    };
  }, []);

  // Events

  function onPagingData(pageNumber: number, pageSize: number): void {
    loadPagination(pageNumber, pageSize);
  }

  function onShowSizeChange(current: any, pageSize: any): void {
    loadPagination(0, pageSize);
  }

  function onSortData(key: string, direction: "asc" | "desc" | null): void {
    const filteredSort = props.multipleSort
      ? sort.filter((sort) => sort.key !== key)
      : [];
    if (direction) {
      loadData([...filteredSort, { key, direction }]);
    } else {
      loadData(filteredSort);
    }
  }

  function onSelect(e: React.MouseEvent, item: any, index: number): void {
    const key = props.rowKey!(item);
    setSelectedKey(key);
  }

  function onDoubleSelect(e: React.MouseEvent, item: any, index: number): void {
    const key = props.rowKey!(item);
    props.onDbSelect?.(key, item, index);
  }

  // Methods

  function loadData(sort: DataSort[]): void {
    setLoading(true);
    setSort(sort);
    setData([]);
    subscribe?.unsubscribe();
    const $$ = subscription(() =>
      props.request({
        context: props.context,
        pageNumber: 0,
        pageSize: props.pageSize,
        sort,
      }),
    );
    setSubscribe($$);
    $$.then((result) => {
      setLoading(false);
      setPagination({
        pageNumber: result.pageNumber,
        pageSize: result.pageSize,
        total: result.total,
      });
      setData(result.data);
    }).catch(() => {
      setLoading(false);
      setPagination({ pageNumber: 0, pageSize: 100, total: 0 });
      setData([]);
    });
  }

  function loadPagination(pageNumber: number, pageSize: number): void {
    setSelectedKey(null);

    if (pageNumber != 0 && pagination.pageSize == pageSize) {
      const hasPage = Math.ceil(data.length / pageSize) >= pageNumber;
      if (hasPage) {
        return;
      }
    }

    subscribe?.unsubscribe();
    const $$ = subscription(() =>
      props.request({ context: props.context, pageNumber, pageSize, sort }),
    );
    setSubscribe($$);
    $$.then((result) => {
      const paging = {
        ...pagination,
        pageNumber,
        pageSize,
      };
      setLoading(false);
      setPagination(paging);
      setData([...data, ...result.data]);
    }).catch(() => {
      setLoading(false);
    });
  }

  const getHeaderHeight = () => {
    const containerElement =
      tableRef.current?.querySelector(".ant-table-header");

    return containerElement?.clientHeight!;
  };

  const columns: any[] = props.columns.map((column) => {
    const sortIcon = () => {
      if (
        sort.some(
          ({ key, direction }) => key === column.key && direction === "asc",
        )
      ) {
        return (
          <Icon
            className="-m-0.5 text-3xl"
            style={{
              color: token.colorPrimaryText,
            }}
            component={Icons.ArrowUpIcon}
            onClick={() => onSortData(column.key, "desc")}
          />
        );
      } else if (
        sort.some(
          ({ key, direction }) => key === column.key && direction === "desc",
        )
      ) {
        return (
          <Icon
            className="-m-0.5 text-3xl"
            style={{
              color: token.colorPrimaryText,
            }}
            component={Icons.ArrowDownIcon}
            onClick={() => onSortData(column.key, null)}
          />
        );
      } else {
        return (
          <Icon
            className="-m-0.5 text-3xl"
            component={Icons.UnfoldMoreIcon}
            onClick={() => onSortData(column.key, "asc")}
          />
        );
      }
    };

    return {
      title: (
        <>
          {!column.onAction && (
            <Typography.Text className="inline-flex w-full flex-row justify-between overflow-hidden">
              <Typography.Text className="flex-auto overflow-hidden whitespace-nowrap pr-2">
                {column.title}
              </Typography.Text>
              {column.sort && sortIcon()}
            </Typography.Text>
          )}
        </>
      ),
      dataIndex: column.key,
      key: column.key,
      width: column.width,
      fixed: column.fixed,
      render: (text: string, record: any) => {
        // Custom full-cell renderer takes precedence (e.g. a coloured band tag).
        if (column.render && !column.onAction) {
          return column.render(text, record);
        }
        // When Action
        if (column.onAction) {
          text = column.title;
        } else {
          if (text != null && column.transform) {
            text = column.transform(text);
          }

          if (text == null) {
            text = "";
          }
          // Date
          else if (column.format && dayjs(text).isValid()) {
            text = column.format ? dayjs(text).format(column.format) : text;
          }
          // Boolean
          else if (typeof text == "boolean") {
            text = !!text ? "Yes" : "No";
          }
        }

        const columnText = () => {
          return (
            <>
              {!column.onAction && <Typography.Text>{text}</Typography.Text>}
              {column.onAction && (
                <Typography.Link
                  onClick={() => column.onAction?.(column.key, record)}
                >
                  {text}
                </Typography.Link>
              )}
            </>
          );
        };

        const controlType = () => {
          switch (column?.type) {
            case "badge":
              return <Tag color={"blue"}>{columnText()}</Tag>;
            case "editorbox":
              return <Editor value={text} inline readOnly></Editor>;

            default:
              return columnText();
          }
        };

        return (
          <Tooltip placement="bottomLeft" title={text}>
            {controlType()}
          </Tooltip>
        );
      },
    };
  });

  if (props.actions) {
    columns.push({
      title: "",
      sort: false,
      width: 42,
      fixed: "right",
      className: "text-end",
      render: (text: string, record: any) => {
        const actions = Array.isArray(props.actions)
          ? props.actions
          : props.actions?.(record) || [];

        return (
          <Dropdown
            placement={"bottomRight"}
            menu={{
              items: actions?.map(({ label, key, danger, disabled }) => ({
                key,
                label,
                danger,
                disabled,
              })) as any[],
              onClick: ({ key, domEvent }) => {
                domEvent.preventDefault();
                domEvent.stopPropagation();
                const { handler } = actions?.find(
                  (action) => action.key === key,
                )!;
                handler?.(key, record);
              },
            }}
          >
            <Button
              className="-mb-1.5 -mt-1.5"
              type={"text"}
              icon={
                <Icon
                  className="-ml-0.5 text-2xl"
                  component={Icons.MoreVertIcon}
                />
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </Dropdown>
        );
      },
    });
  }

  return (
    <Col className="flex flex-auto flex-col overflow-hidden">
      <Col
        ref={tableRef}
        className="relative flex flex-auto flex-col overflow-hidden"
      >
        {tableRef && (
          <Table
            className="my-inline-record absolute bottom-0 left-0 right-0 top-0"
            rowClassName={(item, index) =>
              selectedKey === props.rowKey(item) ? "my-data-row-selected" : ""
            }
            sticky
            virtual
            size="small"
            scroll={{
              y: height,
            }}
            showHeader={!props.hideHeader}
            rowKey={props.rowKey}
            dataSource={data.slice(
              pagination.pageNumber * pagination.pageSize,
              pagination.pageNumber * pagination.pageSize + pagination.pageSize,
            )}
            columns={columns}
            pagination={false}
            onRow={(item, index) => {
              return {
                onClick: (e) => onSelect(e, item, index!),
                onDoubleClick: (e) => onDoubleSelect(e, item, index!),
              };
            }}
          />
        )}
        {loading && (
          <Col className="absolute bottom-0 left-0 right-0 top-0 z-10 flex flex-col items-center justify-center">
            <Loader />
          </Col>
        )}
      </Col>
      <Row className="flex flex-row justify-end pt-2">
        <Pagination
          showSizeChanger
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
          }
          current={pagination.pageNumber}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={onPagingData}
          onShowSizeChange={onShowSizeChange}
        />
      </Row>
    </Col>
  );
}

export default Data;
